import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    FormGroup,
    Tooltip,
    IconButton,
    Skeleton
} from "@mui/material";
import styles from './OverlayBoxes.module.css';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toggleLoader } from "../../store/loader/loaderReducer";
import { useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AppHeader from '../../component/appHeader/AppHeader';

const scopeLabels = [
    "sign.signrequest.READ",
    "sign.signrequest.WRITE",
    "sign.signrequest.UPDATE",
    "sign.signrequest.DELETE",
    "sign.signrequest.ALL",
    "sign.document.READ",
    "sign.document.WRITE",
    "sign.document.UPDATE",
    "sign.document.DELETE",
    "sign.document.ALL",
    "sign.template.READ",
    "sign.template.WRITE",
    "sign.template.UPDATE",
    "sign.template.DELETE",
    "sign.template.ALL",
    "sign.contacts.READ",
    "sign.contacts.WRITE",
    "sign.contacts.UPDATE",
    "sign.contacts.DELETE",
    "sign.contacts.ALL"
];

const OverlayBoxes = () => {
    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState("");
    const [copied, setCopied] = useState(false);
    const [scopes, setScopes] = useState({
        sign: false,
        module: false,
        template: false,
        all: false
    });
    const [showSecret, setShowSecret] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [initialFetching, setInitialFetching] = useState(true);

    const dispatch = useDispatch();
    const tokenRef = useRef(null);

    useEffect(() => {
        if (token && token.length > 0 && tokenRef.current) {
            tokenRef.current.focus();
        }
    }, [token]);
    useEffect(() => {
        const savedScopes = localStorage.getItem("overlay_scopes");
        const savedToken = localStorage.getItem("overlay_token");
        if (savedScopes) {
            try {
                const parsed = JSON.parse(savedScopes);
                if (parsed && typeof parsed === "object") {
                    setScopes(parsed);
                }
            } catch (_) {}
        }
        if (savedToken) {
            setToken(savedToken);
        }
    }, []);

    useEffect(() => {
        dispatch(toggleLoader(true));
        axios.get(process.env.REACT_APP_OAUTH_CLIENT_CREDENTIALS, {
            withCredentials: process.env.REACT_APP_CREDENTIALS,
        })
            .then((res) => {
                if (res?.data?.data[0]?.Id) {
                    setClientData({
                        client_id: res?.data?.data[0]?.Client_Id,
                        client_secret: res?.data?.data[0]?.Client_Secret,
                    });
                    setLoading(true);
                }
            })
            .catch((err) => toast.error(err.message))
            .finally(() => {
                setInitialFetching(false);
                dispatch(toggleLoader(false));
            });
    }, []);

    const handleScopeChange = (event) => {
        const { name, checked } = event.target;
        setScopes((prev) => {
            const updated = { ...prev, [name]: checked };
            try {
                localStorage.setItem("overlay_scopes", JSON.stringify(updated));
            } catch (_) {}
            return updated;
        });
    };

    const handleGenerate = async () => {
        setLoading(true);

        const formData = { app_name: "My Application", scope: "sign.ALL", redirect_uri: "http://localhost:8080/sign", type: 2 };

        dispatch(toggleLoader(true));
        axios.post(process.env.REACT_APP_OAUTH_APP, formData, { withCredentials: process.env.REACT_APP_CREDENTIALS })
            .then((res) => {
                setClientData(res?.data);
                dispatch(toggleLoader(false));
            })
            .catch((err) => {
                toast.error(err.message);
                dispatch(toggleLoader(false));
            });
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(token);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const handleRevoke = () => {
        dispatch(toggleLoader(true));
        axios.post(process.env.REACT_APP_OAUTH_REVOKE, { token: token }, { withCredentials: process.env.REACT_APP_CREDENTIALS })
            .then((res) => {
                setToken("");
                setCopied(false);
                try {
                    localStorage.removeItem("overlay_token");
                } catch (_) {}
                dispatch(toggleLoader(false));
            })
            .catch((err) => {
                toast.error(err.message);
                dispatch(toggleLoader(false));
            });
    };

    const handleGenerateToken = () => {
        const trueKeys = Object.keys(scopes).filter(key => scopes[key]);
        const formData = { client_id: clientData?.client_id, scope: trueKeys, client_secret: clientData?.client_secret };

        if (!trueKeys?.length) {
            toast.warning("Please select a scope before generating the token");
            return;
        }

        dispatch(toggleLoader(true));
        axios.post(process.env.REACT_APP_OAUTH_CLIENT_CREDENTIALS, formData, { withCredentials: process.env.REACT_APP_CREDENTIALS })
            .then((res) => {
                const accessToken = res?.data?.data?.access_token;
                setToken(accessToken);
                try {
                    if (accessToken) {
                        localStorage.setItem("overlay_token", accessToken);
                    }
                } catch (_) {}
                dispatch(toggleLoader(false));
            })
            .catch((err) => {
                toast.error(err.message);
                dispatch(toggleLoader(false));
            });
    };

    return (
        <Box sx={{ padding: '0px 30px 30px' }} className={styles.container}>
            {initialFetching ? (
                <Skeleton
                    sx={{ bgcolor: "rgba(189,197,209,.2)", margin: "10px" }}
                    animation="wave"
                    variant="rounded"
                    width="calc(100% - 20px)"
                    height="calc(100vh - 80px)"
                />
            ) : (
                <>
                    <AppHeader
                        moduleText='Generate API Token'
                        rolePermissions={true}
                        checkAdmin={true}
                    />

                    <Typography fontSize="14px" fontFamily="var(--primary-font-family)" color="var(--table-font-color)" sx={{ lineHeight: 1.6, mb: 3, mt: 2 }} paragraph>
                        Use this page to create client credentials and generate an OAuth access
                        token for authorized API calls. First, generate a Client ID and Client
                        Secret for your application. Then select the required scopes and click
                        Generate Token to receive an access token you can use in both
                        development and production environments.
                    </Typography>

                    <Button
                        variant="contained"
                        sx={{ mb: 3, textTransform: "capitalize", borderRadius: '8px', padding: '10px 24px' }}
                        onClick={handleGenerate}
                        disabled={loading}
                    >
                        Generate
                    </Button>

                    {clientData && (
                        <Box>
                            <Box className={styles.card}>
                                <h3 className={styles.cardHeader}>Client Credentials</h3>

                                <Box className={styles.cardBody}>
                                    <Box className={styles.credentialRow}>
                                        <Box className={styles.credentialLabel}>Client ID:</Box>
                                        <Box className={styles.credentialInput}>
                                            {clientData?.client_id}
                                        </Box>
                                    </Box>

                                    <Box className={styles.credentialRow}>
                                        <Box className={styles.credentialLabel}>Client Secret:</Box>
                                        <Box className={styles.credentialInput} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", position: "relative" }}>
                                            {showSecret ? clientData?.client_secret : "••••••••••••••••"}
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowSecret(!showSecret)}
                                                className={styles.visibilityButton}
                                            >
                                                {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Box className={styles.card}>
                                <h3 className={styles.cardHeader}>Scopes</h3>

                                <Box className={styles.scopesContainer}>
                                    {scopeLabels.map((label) => (
                                        <Box
                                            key={label}
                                            className={styles.scopeItem}
                                            onClick={() => {
                                                handleScopeChange({ target: { name: label, checked: !scopes[label] } });
                                            }}
                                        >
                                            <Checkbox
                                                name={label}
                                                checked={!!scopes[label]}
                                                onChange={handleScopeChange}
                                                className={styles.scopeCheckbox}
                                                sx={{ padding: 0 }}
                                            />
                                            <Box className={styles.scopeLabel}>{label}</Box>
                                        </Box>
                                    ))}
                                </Box>

                                <Box className={styles.scopeFooter}>
                                    <Button
                                        variant="contained"
                                        sx={{ textTransform: "capitalize" }}
                                        onClick={handleGenerateToken}
                                    >
                                        Generate Token
                                    </Button>
                                </Box>
                            </Box>
                            {token &&
                                <Box className={styles.card}>
                                    <Box className={styles.cardBody}>
                                        <Box className={styles.credentialRow}>
                                            <Box className={styles.credentialLabel}>Access Token:</Box>
                                            <Box
                                                className={styles.credentialInput}
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: '100%',
                                                    position: "relative"
                                                }}
                                            >
                                                {showToken ? token : "••••••••••••••••"}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowToken(!showToken)}
                                                    className={styles.visibilityButton}
                                                >
                                                    {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Button
                                            ref={tokenRef}
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            sx={{ mt: 2, textTransform: "capitalize", fontSize: "13px" }}
                                            onClick={handleRevoke}
                                            disabled={!token}
                                        >
                                            Revoke Token
                                        </Button>
                                    </Box>
                                </Box>}
                        </Box>
                    )}
                </>)}
        </Box>
    );
};

export default OverlayBoxes;
